import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Button, FormControlLabel, MenuItem, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, TableSortLabel, TextField, Typography,
} from '@mui/material';
import PageLayout from '../shell/PageLayout';
import ScopeFilters, { useScope, rangeUtc } from './ScopeFilters';
import { request } from './OperationsProvider';
import { exportCsv } from './csv';
import brand from '../shell/brand';
import saveFile from '../shell/saveFile';

const OperationalReportsPage = () => {
  const scope = useScope();
  const [page, setPage] = useState(0); const [pageSize, setPageSize] = useState(25); const [sort, setSort] = useState(0); const [order, setOrder] = useState('asc');
  // The report lives in the address, not in component state: a briefing finding that states a
  // number has to be able to open the rows behind it, and that means a link.
  const [params, setParams] = useSearchParams();
  const report = params.get('report') || 'trips';
  const setReport = (value) => { const next = new URLSearchParams(params); next.set('report', value); setParams(next); };
  // Whether the export arrives as one list or as a block per shift. The preview below stays a flat
  // table either way: it is paged and sorted by the reader, and a grouping drawn into a paged table
  // would be a heading on page one describing rows on page four.
  const [byShift, setByShift] = useState(false);
  const [result, setResult] = useState(null); const [error, setError] = useState(null); const [pending, setPending] = useState(false); const [loading, setLoading] = useState(false);
  const query = new URLSearchParams({
    report, site: scope.site, route: scope.route, vehicle: scope.vehicle, movement: scope.movement, direction: scope.direction, transporter: scope.transporter, material: scope.material, origin: scope.origin, destination: scope.destination,
  });
  // The range the filters hold, to the minute where one was given and to the whole day where it was not.
  const { fromUtc, toUtc } = rangeUtc(scope.from, scope.to);
  if (fromUtc) query.set('fromUtc', fromUtc);
  if (toUtc) query.set('toUtc', toUtc);
  const queryString = query.toString();
  useEffect(() => { setPage(0); setSort(0); }, [queryString]);
  const previewQuery = `${queryString}&offset=${page * pageSize}&limit=${pageSize}&sort=${sort}&order=${order}`;
  useEffect(() => { const controller = new AbortController(); setLoading(true); setError(null); if (scope.invalid) { setResult(null); setLoading(false); return () => controller.abort(); } request(`/api/operations/reports?${previewQuery}`, 'GET', null, controller.signal).then(setResult).catch((e) => { if (e.name !== 'AbortError') { setError(e.message); setResult(null); } }).finally(() => { if (!controller.signal.aborted) setLoading(false); }); return () => controller.abort(); }, [previewQuery, scope.invalid]);
  const download = async (format) => {
    setPending(true); setError(null);
    try {
      const response = await fetch(`/api/operations/reports/export?${queryString}&format=${format}${byShift ? '&groupBy=shift' : ''}`);
      if (!response.ok) throw new Error((await response.json()).message || 'Export failed.');
      await saveFile(`${brand.key}-${report}.${format}`, await response.blob());
    } catch (e) { setError(e.message); } finally { setPending(false); }
  };
  const rows = result?.total ?? 0;
  const counted = report === 'billing' ? `${Math.max(rows - 1, 0)} days with movement · the Total row is the figure to invoice`
    : report === 'billing-detail' ? `${rows} billable trips`
      : report === 'turnaround' ? `${rows} trips that entered and left in this period · the average of this column is the briefing's turnaround`
        : report === 'exceptions' ? `${rows} exceptions raised in this period · acknowledged and resolved ones included`
          : `${rows} records`;
  return (
    <PageLayout title="Reports" subtitle="Filter, review and export your authorized operational records.">
      <ScopeFilters scope={scope} showArea={false} dateLabel={report === 'trips' ? 'Trip creation date' : report.startsWith('billing') ? 'Gate event date' : report === 'turnaround' ? 'Gate in date' : report === 'exceptions' ? 'Exception occurrence date' : 'Checkpoint occurrence date'} />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
          <TextField label="Report type" select value={report} onChange={(e) => setReport(e.target.value)} sx={{ minWidth: 220 }}>
            <MenuItem value="trips">Trip detail</MenuItem>
            <MenuItem value="checkpoints">Checkpoint segments</MenuItem>
            <MenuItem value="turnaround">Turnaround (slowest first)</MenuItem>
            <MenuItem value="billing">Billable trips (daily)</MenuItem>
            <MenuItem value="billing-detail">Billable trips (itemised)</MenuItem>
            <MenuItem value="exceptions">Exception history</MenuItem>
          </TextField>
          <Button variant="contained" disabled={pending || loading || scope.invalid || !result} onClick={() => download('pdf')}>Export PDF</Button>
          <Button variant="outlined" disabled={pending || loading || scope.invalid || !result} onClick={() => download('xlsx')}>Export Excel</Button>
          <Button disabled={pending || loading || !result} onClick={() => exportCsv(`${brand.key}-${report}-preview`, result.columns, result.rows.map((r) => Object.fromEntries(result.columns.map((c, i) => [c, r[i]]))))}>CSV preview</Button>
          {/* Only where the report carries a shift to group by. */}
          {(result?.columns || []).includes('Shift') && (
            <FormControlLabel
              control={<Switch size="small" checked={byShift} onChange={(e) => setByShift(e.target.checked)} />}
              label={<Typography variant="body2">Group by shift</Typography>}
            />
          )}
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{loading ? 'Loading records…' : `${counted} · Preview is paged · PDF and Excel export matching records, up to 20,000 rows · Every time is IST; the date filter uses your own day`}</Typography>
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader>
            <TableHead><TableRow>{result?.columns.map((c, index) => <TableCell key={c} sx={{ whiteSpace: 'nowrap' }}><TableSortLabel active={sort === index} direction={order} onClick={() => { setSort(index); setOrder(sort === index && order === 'asc' ? 'desc' : 'asc'); setPage(0); }}>{c}</TableSortLabel></TableCell>)}</TableRow></TableHead>
            <TableBody>
              {result?.rows.map((row) => (
                <TableRow key={JSON.stringify(row)}>
                  {/* A zero turnaround, a zero variance and a zero count are all answers; only an absent
                  value is an em dash. `||` turned every one of them into "no data". */}
                  {row.map((v, c) => <TableCell key={result.columns[c]} sx={{ whiteSpace: 'nowrap' }}>{v === null || v === undefined || v === '' ? '—' : v}</TableCell>)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={rows} page={page} rowsPerPage={pageSize} rowsPerPageOptions={[25, 50, 100]} onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }} />
      </Paper>
    </PageLayout>
  );
};
export default OperationalReportsPage;
